import { useCallback, useEffect, useState } from 'react';
import { Button, Spin } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { getStatus } from '@/api/dataroom';
import { normalizeError, type NormalizedError } from '@/api/errors';
import type { StatusResponse } from '@/api/types';
import { OverallBanner } from '@/components/OverallBanner';
import { PageError } from '@/components/PageError';
import { RoomShell } from '@/components/RoomShell';
import { StatusStepper } from '@/components/StatusStepper';
import { useI18n } from '@/i18n/useI18n';
import { formatDateTime } from '@/lib/format';
import { useRoomNav } from '@/session/useRoomNav';

/**
 * The status view (F-IS-08 "kills silent-wait abandonment"). A calm, reassuring stepper of where the
 * case stands, in plain language. It NEVER shows risk or suspicion — the backend guarantees the shape
 * is tipping-off-safe; the client just renders the progress it is given.
 */
export function StatusPage() {
  const { t, localeTag, dir } = useI18n();
  const nav = useRoomNav();
  const [data, setData] = useState<StatusResponse | null>(null);
  const [error, setError] = useState<NormalizedError | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await getStatus());
    } catch (err) {
      setError(normalizeError(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  let body;
  if (data === null && error) {
    body = <PageError error={error} onRetry={load} />;
  } else if (data === null) {
    body = (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
        <Spin size="large" />
      </div>
    );
  } else {
    body = (
      <>
        <div className="dr-intro">
          <h1 className="dr-intro__h">{t('status.heading')}</h1>
          <p className="dr-intro__p">{t('status.reassure')}</p>
        </div>

        <OverallBanner status={data.overallStatus} />

        <div className="dr-card" style={{ marginBottom: 16 }}>
          <StatusStepper steps={data.steps} />
        </div>

        <div className="dr-meta" style={{ marginBottom: 20 }}>
          {t('status.lastUpdated', { when: formatDateTime(data.lastUpdatedAt, localeTag) })}
        </div>

        <Button
          block
          size="large"
          icon={dir === 'rtl' ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
          onClick={() => nav('/tasks')}
        >
          {t('status.backToTasks')}
        </Button>
      </>
    );
  }

  return <RoomShell>{body}</RoomShell>;
}
